import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { MediaQuery, Palette } from 'core/config';
import { SPEER_DEFAULT_META_DESCRIPTION } from 'core/utilities';
import { CanonicalTag } from 'view/components/common';
import IMAGES from 'assets/images';
import { showCaseImages, emrShowcaseImages } from 'view/pages/Home/data/showCaseImages.json';
import { ClientLogoMarquee } from 'view/pages/Webinar/sections/ClientLogoMarquee';
import { StrategyCallForm } from './StrategyCallForm';

const LOGO_MARQUEE_ITEMS = [
    ...showCaseImages.map((item) => ({
        type: 'image' as const,
        src: IMAGES[item.imageKey],
        alt: item.imageKey,
    })),
    ...emrShowcaseImages.map((item) => ({
        type: 'image' as const,
        src: IMAGES[item.imageKey],
        alt: item.imageKey,
    })),
];

const PAGE_TITLE = 'Speer Technologies | Software & AI Engineering for Startups';
const PAGE_DESCRIPTION = SPEER_DEFAULT_META_DESCRIPTION;
const CANONICAL_URL = 'https://www.speer.io/contact';

const HERO_TITLE = 'Your AI Product and Software Partner.';
const HERO_SUBTITLE =
    'Designing and developing digital products and experiences for innovative companies, globally.';

export const ExposureLanding: React.FunctionComponent = () =>
{
    return (
        <Container>
            <Helmet>
                <title>{PAGE_TITLE}</title>
                <meta name="description" content={PAGE_DESCRIPTION} />
                <meta property="og:title" content={PAGE_TITLE} />
                <meta property="og:description" content={PAGE_DESCRIPTION} />
                <meta name="twitter:title" content={PAGE_TITLE} />
                <meta name="twitter:description" content={PAGE_DESCRIPTION} />
            </Helmet>
            <CanonicalTag url={CANONICAL_URL} />

            <HeroSection>
                <HeroInner className="fade-in">
                    <HeroGrid>
                        <LeftColumn>
                            <TextWrap>
                                <Title>{HERO_TITLE}</Title>
                                <Subtitle>{HERO_SUBTITLE}</Subtitle>
                            </TextWrap>
                        </LeftColumn>

                        <RightColumn>
                            <StrategyCallForm />
                        </RightColumn>
                    </HeroGrid>

                    <LogoMarqueeWrap>
                        <ClientLogoMarquee
                            title="Our Partners"
                            items={LOGO_MARQUEE_ITEMS}
                            marqueeEnabled
                            repeatItems
                        />
                    </LogoMarqueeWrap>
                </HeroInner>
            </HeroSection>
        </Container>
    );
};

const fadeIn = keyframes`
    0% { opacity: 0 }
    100% { opacity: 1 }
`;

const Container = styled.div`
    overflow: hidden;
    background: ${Palette.WHITE};
`;

const HeroSection = styled.section`
    display: flex;
    align-items: flex-start;
    padding: 72px 16px 0;

    @media ${MediaQuery.tablet} {
        padding: 80px 0 0;
    }
`;

const LogoMarqueeWrap = styled.div`
    margin-top: 24px;

    @media ${MediaQuery.tablet} {
        margin-top: 32px;
    }

    @media ${MediaQuery.laptopS} {
        margin-top: 40px;
    }

    & > section {
        padding: 12px 0 28px;
        border-bottom: none;
    }
`;

const HeroInner = styled.div`
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 8px;

    &.fade-in {
        animation: ${fadeIn} 0.5s ease-in;
    }

    @media ${MediaQuery.tablet} {
        padding: 0 48px;
    }

    @media ${MediaQuery.laptopS} {
        padding: 0 64px 0 40px;
    }
`;

const HeroGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 48px;

    @media ${MediaQuery.laptopS} {
        flex-direction: row;
        align-items: flex-start;
        gap: 40px;
    }
`;

const LeftColumn = styled.div`
    flex: 1;
    min-width: 0;
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    text-align: left;
    padding-top: 8px;

    @media ${MediaQuery.laptopS} {
        padding-left: 0;
        padding-right: 24px;
        padding-top: 16px;
    }
`;

const TextWrap = styled.div`
    max-width: 540px;
    width: 100%;
    text-align: left;

    @media ${MediaQuery.laptopS} {
        max-width: 480px;
    }
`;

const RightColumn = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 420px;
    margin: 0 auto;

    @media ${MediaQuery.laptopS} {
        margin: 0;
        justify-content: flex-end;
    }
`;

const Title = styled.h1`
    color: ${Palette.BLACK};
    font-size: calc(1.325em + 4vw);
    line-height: 1.15;
    margin: 0;

    @media ${MediaQuery.tablet} {
        font-size: 2.75em;
    }

    @media ${MediaQuery.laptopS} {
        font-size: 3.375em;
    }

    @media ${MediaQuery.desktopS} {
        font-size: 4em;
    }
`;

const Subtitle = styled.p`
    color: ${Palette.BLACK};
    font-family: 'Poppins-Light', sans-serif;
    font-size: 16px;
    line-height: 1.8;
    margin: 24px 0 0;
    max-width: 540px;

    @media ${MediaQuery.tablet} {
        font-size: 18px;
    }

    @media ${MediaQuery.laptopS} {
        font-size: 20px;
        margin-top: 14px;
    }
`;
